import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Avatar from "@mui/material/Avatar";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotesIcon from "@mui/icons-material/Notes";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { useState } from "react";
import { Link, ReferenceField, useGetList, useGetOne } from "react-admin";
import { useQuery, useQueryClient } from "react-query";
import { z } from "zod";
import { currentUser, grants } from "../../backoffice.access_control";
import { getAuthenticatedServerClient } from "../../api/server.api";
import { schemas } from "../../generated/serverClient";
import { CourseLesson } from "../../model/autovioCalendarEvents";
import { Student } from "../../providers/studentsProvider";
import { Course } from "../../providers/coursesProvider";
import { Branch } from "../../providers/branchesProvider";
import { formatDate } from "../../utils/calendar";
import { Column } from "../../misc/Column";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { DialogTopRightActions } from "../../misc/DialogTopRightActions";
import { Row } from "../../misc/Row";
import { StudentLabels } from "../../misc/StudentLabels";
import { UserDisplayName } from "../../misc/UserDisplayName";
import { useMenu } from "../../hooks/useMenu";
import { useDialog } from "../../hooks/useDialog";
import { useThemeColor } from "../../hooks/useThemeColor";
import { ConfirmDialog } from "../ConfirmDialog";
import { EditCourseLessonForm } from "./EditCourseLessonForm";
import { LessonProgressTimeline } from "./LessonProgressTimeline";

type AttendanceStatus = z.infer<typeof schemas.CourseLessonAttendanceStatusEnum>;

export function CourseLessonDialog(props: { event: CourseLesson; onClose: () => void; initialMode?: "view" | "edit" }) {
  const { event, onClose, initialMode } = props;
  const [mode, setMode] = useState<"view" | "edit">(initialMode ?? "view");
  const [changes, setChanges] = useState<Record<string, AttendanceStatus>>({});
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const canEdit = !event.deleted && (grants.has("calendarEventEdit") || event.instructorId === currentUser?.uid);
  const canDelete = !event.deleted && grants.has("CourseLesson:delete");
  const deleteDialog = useDialog();
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  const save = async () => {
    setSaving(true);
    await runSafely(
      async () =>
        (await getAuthenticatedServerClient()).setCourseLessonAttendances(changes, { params: { id: event.id } }),
      {
        errorMessage: "Anwesenheiten konnten nicht gespeichert werden.",
        successMessage: "Anwesenheiten gespeichert.",
        onSuccess: async () => {
          await queryClient.invalidateQueries(["courseLessonAttendances", event.id]);
          setChanges({});
        },
      },
    );
    setSaving(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth data-testid="EventDialog">
      <DialogTitle>{mode === "edit" ? "Termin bearbeiten" : `Unterricht am ${formatDate(event.start)}`}</DialogTitle>
      {mode === "view" && (canEdit || canDelete) && (
        <DialogTopRightActions>
          <IconButton aria-label="Weitere Aktionen" onClick={openMenu}>
            <MoreVertIcon sx={{ color: "text.secondary" }} />
          </IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
            {canEdit && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  setMode("edit");
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Termin bearbeiten</ListItemText>
              </MenuItem>
            )}
            {canDelete && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  deleteDialog.openDialog();
                }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Termin löschen</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </DialogTopRightActions>
      )}
      <DialogCloseButton onClick={onClose} />
      {canDelete && (
        <ConfirmDialog
          {...deleteDialog.dialogProps}
          title="Termin löschen?"
          message="Bist du dir sicher, dass der Termin gelöscht werden soll? Diese Aktion kann nicht rückgängig gemacht werden."
          confirmButtonLabel="Ja, Termin löschen"
          onConfirm={async () => {
            await runSafely(() => dataProvider.delete("courseLessons", { id: event.id }), {
              errorMessage: "Fehler beim Löschen des Termins.",
              successMessage: "Termin erfolgreich gelöscht.",
              onSuccess: onClose,
            });
          }}
        />
      )}
      <DialogContent>
        {mode === "edit" ? (
          <EditCourseLessonForm event={event} closeDialog={onClose} />
        ) : (
          <Stack spacing={2}>
            <LessonProgressTimeline event={event} />
            <CourseLessonInfo event={event} />
            {grants.has("CourseLesson:getAttendance") ? (
              <CourseLessonAttendance event={event} changes={changes} setChanges={setChanges} saving={saving} />
            ) : (
              <Typography color="text.secondary">Keine Berechtigung, Anwesenheiten einzusehen.</Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      {mode === "view" && grants.has("CourseLesson:setAttendance") && Object.keys(changes).length > 0 && (
        <DialogActions sx={{ mt: "20px" }}>
          <LoadingButton loading={saving} variant="contained" onClick={() => void save()}>
            Anwesenheit speichern
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

function CourseLessonInfo(props: { event: CourseLesson }) {
  const { event } = props;
  const { data: branch, isError: branchError } = useGetOne<Branch>(
    "branches",
    { id: event.branchId! },
    { enabled: !!event.branchId && event.location.type !== "OnlineMeeting" },
  );
  const start = event.start.setZone("Europe/Berlin");
  const end = event.end.setZone("Europe/Berlin");
  const timeRange = `${start.toFormat("H:mm")} - ${end.toFormat("H:mm")} Uhr`;

  return (
    <Stack spacing={2} sx={{ mt: "20px" }}>
      <Row
        sx={{
          alignItems: "flex-start",
          flexWrap: "wrap",
          borderRadius: "16px",
          backgroundColor: "action.hover",
          px: "24px",
          pt: "16px",
          pb: "12px",
          columnGap: "40px",
          rowGap: "16px",
          color: "text.secondary",
        }}
      >
        <Row sx={{ alignItems: "flex-start", flex: "1 1 240px", minWidth: 0, gap: "12px" }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: event.courseModuleColor,
              mt: "6px",
              flexShrink: 0,
            }}
          />
          <Column sx={{ minWidth: 0, gap: "2px" }}>
            <Typography fontWeight={700} fontSize="14px">
              {event.courseModuleName}
            </Typography>
            <Typography fontSize="14px">{timeRange}</Typography>
          </Column>
        </Row>
        <Row sx={{ alignItems: "flex-start", flex: "1 1 240px", minWidth: 0, gap: "12px" }}>
          <CalendarTodayOutlinedIcon sx={{ mt: "4px", flexShrink: 0, fontSize: "18px" }} />
          <Column sx={{ minWidth: 0, gap: "2px" }}>
            <Typography fontWeight={700} fontSize="14px">
              {start.setLocale("de-DE").toFormat("ccc, dd.MM.yyyy")}
            </Typography>
            <Typography fontSize="14px">{timeRange}</Typography>
          </Column>
        </Row>
        <Row sx={{ alignItems: "flex-start", flex: "1 1 240px", minWidth: 0, gap: "12px" }}>
          <PlaceOutlinedIcon sx={{ mt: "4px", flexShrink: 0, fontSize: "18px" }} />
          <Column sx={{ minWidth: 0, gap: "2px" }}>
            <Typography
              fontWeight={event.location.type === "OnlineMeeting" ? 700 : 600}
              fontSize="14px"
              sx={{ overflowWrap: "anywhere" }}
            >
              {event.location.type === "OnlineMeeting"
                ? "Online"
                : !event.branchId
                  ? "Keine Filiale zugewiesen"
                  : branchError
                    ? "Filiale konnte nicht geladen werden."
                    : (branch?.name ?? "Filiale wird geladen...")}
            </Typography>
            <Typography fontSize="14px" sx={{ overflowWrap: "anywhere" }}>
              {event.location.type === "OnlineMeeting" ? (
                event.location.url
              ) : event.roomId ? (
                <ReferenceField reference="rooms" record={event} source="roomId" link={false} />
              ) : (
                "Noch kein Raum zugewiesen"
              )}
            </Typography>
          </Column>
        </Row>
        <Row sx={{ alignItems: "flex-start", flex: "1 1 240px", minWidth: 0, gap: "12px" }}>
          <PersonOutlineOutlinedIcon sx={{ mt: "4px", flexShrink: 0, fontSize: "18px" }} />
          <Column sx={{ minWidth: 0, gap: "2px" }}>
            <Typography fontWeight={700} fontSize="14px" sx={{ overflowWrap: "anywhere" }}>
              {event.lecturerId ? (
                <UserDisplayName userId={event.lecturerId} variant="body2" fontWeight={600} />
              ) : (
                "Noch kein Dozent zugewiesen"
              )}
            </Typography>
            <Typography fontSize="14px">Dozent/Fahrlehrer</Typography>
          </Column>
        </Row>
      </Row>
      {event.noteForParticipants && (
        <Row sx={{ alignItems: "center", gap: 1 }}>
          <NotesIcon fontSize="small" />
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {event.noteForParticipants}
          </Typography>
        </Row>
      )}
    </Stack>
  );
}

function CourseLessonAttendance(props: {
  event: CourseLesson;
  changes: Record<string, AttendanceStatus>;
  setChanges: React.Dispatch<React.SetStateAction<Record<string, AttendanceStatus>>>;
  saving: boolean;
}) {
  const { event, changes, setChanges, saving } = props;
  const canSetAttendance = grants.has("CourseLesson:setAttendance") && !event.deleted;
  const successColor = useThemeColor("success").main;
  const errorColor = useThemeColor("error").main;
  const { data: course } = useGetOne<Course>("courses", {
    id: event.courseId,
    meta: { isBKFCourse: true },
  });
  const {
    data: participants,
    isLoading: participantsLoading,
    isError: participantsError,
    refetch: refetchParticipants,
  } = useGetList<Student>("courseParticipants", {
    filter: { courseId: event.courseId },
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "name", order: "ASC" },
  });
  const {
    data: attendances,
    isLoading: attendancesLoading,
    isError: attendancesError,
    refetch: refetchAttendances,
  } = useQuery(
    ["courseLessonAttendances", event.id],
    async () => (await getAuthenticatedServerClient()).getCourseLessonAttendances({ params: { id: event.id } }),
    { refetchOnMount: "always" },
  );

  return (
    <Stack spacing={2}>
      {(participantsLoading || attendancesLoading) && (
        <Typography color="text.secondary">Lade Teilnehmer...</Typography>
      )}
      {(participantsError || attendancesError) && (
        <Alert
          severity="error"
          action={
            <Button
              onClick={() => {
                void refetchParticipants();
                void refetchAttendances();
              }}
            >
              Erneut laden
            </Button>
          }
        >
          Teilnehmer oder Anwesenheiten konnten nicht geladen werden.
        </Alert>
      )}
      {participants && attendances && !participantsError && !attendancesError && (
        <>
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: "12px", overflowX: "auto" }}>
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "action.hover",
                      color: "text.secondary",
                      fontWeight: 700,
                      py: "10px",
                      px: "16px",
                    }}
                  >
                    Teilnehmer
                  </TableCell>
                  <TableCell
                    sx={{ display: { xs: "none", md: "table-cell" }, backgroundColor: "action.hover", width: 140 }}
                  >
                    Kurs
                  </TableCell>
                  <TableCell
                    sx={{ display: { xs: "none", md: "table-cell" }, backgroundColor: "action.hover", width: 200 }}
                  >
                    Hinweise
                  </TableCell>
                  <TableCell
                    sx={{ display: { xs: "none", md: "table-cell" }, backgroundColor: "action.hover", width: 150 }}
                  >
                    Check-In Teilnehmer
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "action.hover",
                      color: "text.secondary",
                      fontWeight: 700,
                      width: { xs: 92, sm: 170 },
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      Anwesenheit bestätigt
                    </Box>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "action.hover", width: 36, p: 0 }} />
                  <TableCell sx={{ backgroundColor: "action.hover", width: 8, p: 0 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {participants.length === 0 && (
                  <TableRow>
                    <TableCell sx={{ px: "16px", py: "12px" }}>Keine Teilnehmer eingeschrieben.</TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }} />
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }} />
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }} />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
                {participants.map((student) => {
                  const attendance = attendances[student.id];
                  const status = changes[student.id] ?? attendance?.status ?? null;
                  const checkIn =
                    attendance?.checkIn === "participantClaimedPresence"
                      ? "Anwesenheit gemeldet"
                      : attendance?.checkIn === "participantReportedAbsence"
                        ? "Abwesenheit gemeldet"
                        : null;
                  const indicatorColor =
                    status === "instructorConfirmedAttendance"
                      ? successColor
                      : status === "instructorConfirmedAbsence"
                        ? errorColor
                        : "transparent";
                  const notes = [
                    event.noteForParticipants,
                    attendance?.minutesTooLate != null ? `${attendance.minutesTooLate} Min. verspätet` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <TableRow key={student.id}>
                      <TableCell sx={{ px: { xs: "8px", sm: "16px" }, py: "8px" }}>
                        <Link
                          to={`/students/${student.id}`}
                          sx={{ display: "flex", alignItems: "center", color: "inherit", minWidth: 0 }}
                        >
                          <Avatar src={student.avatarUrl} sx={{ width: 32, height: 32 }} />
                          <Column sx={{ ml: "12px", minWidth: 0 }}>
                            <StudentLabels student={student} size="small" />
                            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                              {student.name}
                            </Typography>
                            <Box sx={{ display: { xs: "block", md: "none" } }}>
                              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                {[course?.name, notes, checkIn].filter(Boolean).join(" · ")}
                              </Typography>
                            </Box>
                          </Column>
                        </Link>
                      </TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" }, width: 140 }}>
                        {course?.name ?? "-"}
                      </TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", md: "table-cell" }, width: 200, overflowWrap: "anywhere" }}
                      >
                        {notes || "-"}
                      </TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" }, width: 150 }}>
                        {checkIn || "-"}
                      </TableCell>
                      <TableCell sx={{ width: { xs: 92, sm: 170 }, px: "8px", py: 0, textAlign: "right" }}>
                        {canSetAttendance ? (
                          <Row sx={{ gap: "12px", my: "8px", justifyContent: "flex-end" }}>
                            <IconButton
                              aria-label={`Abwesend: ${student.name}`}
                              disabled={saving}
                              onClick={() =>
                                setChanges((previous) => ({ ...previous, [student.id]: "instructorConfirmedAbsence" }))
                              }
                              sx={{
                                backgroundColor:
                                  status === "instructorConfirmedAbsence" ? "error.main" : "action.hover",
                                width: 24,
                                height: 24,
                              }}
                            >
                              <CloseIcon
                                sx={{
                                  color:
                                    status === "instructorConfirmedAbsence" ? "error.contrastText" : "text.disabled",
                                  width: 16,
                                  height: 16,
                                }}
                              />
                            </IconButton>
                            <IconButton
                              aria-label={`Anwesend: ${student.name}`}
                              disabled={saving}
                              onClick={() =>
                                setChanges((previous) => ({
                                  ...previous,
                                  [student.id]: "instructorConfirmedAttendance",
                                }))
                              }
                              sx={{
                                backgroundColor:
                                  status === "instructorConfirmedAttendance" ? "success.main" : "action.hover",
                                width: 24,
                                height: 24,
                              }}
                            >
                              <CheckIcon
                                sx={{
                                  color:
                                    status === "instructorConfirmedAttendance"
                                      ? "success.contrastText"
                                      : "text.disabled",
                                  width: 16,
                                  height: 16,
                                }}
                              />
                            </IconButton>
                          </Row>
                        ) : status === "instructorConfirmedAttendance" ? (
                          "Anwesend"
                        ) : status === "instructorConfirmedAbsence" ? (
                          "Abwesend"
                        ) : (
                          "Offen"
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 36, p: 0, textAlign: "center" }}>
                        <IconButton disabled aria-label={`Weitere Aktionen für ${student.name}`} size="small">
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell
                        sx={{
                          width: 8,
                          p: 0,
                          backgroundColor: indicatorColor,
                        }}
                      />
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="body2" color="text.disabled">
            {participants.length} Teilnehmer
          </Typography>
        </>
      )}
    </Stack>
  );
}
